"use client"

import { useState, useEffect } from "react"
import { useSearchParams, useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress } from "@mui/material"
import { Link } from "react-router-dom"
import BaseURL from "../../api/AppURL";

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { token: routeToken } = useParams() // URL প্যারামিটার থেকে টোকেন নেওয়া হচ্ছে
  const email = searchParams.get("email") // URL কোয়েরি প্যারামিটার থেকে ইমেল নেওয়া হচ্ছে

  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [isValidLink, setIsValidLink] = useState(true) // লিংক বৈধ কিনা তা চেক করার জন্য

  // টোকেন এবং ইমেল URL এ আছে কিনা তা নিশ্চিত করুন
  useEffect(() => {
    if (!routeToken || !email) {
      setError("Invalid or incomplete password reset link.")
      setIsValidLink(false)
    }
  }, [routeToken, email])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMessage("")
    setError("")
    setLoading(true)

    if (!isValidLink) {
      setLoading(false)
      return // যদি লিংক বৈধ না হয়, সাবমিট করবেন না
    }

    try {
      const res = await axios.post(`${BaseURL}/reset-password`, {
        token: routeToken, // URL থেকে প্রাপ্ত টোকেন
        email: email, // URL থেকে প্রাপ্ত ইমেল
        password: password,
        password_confirmation: passwordConfirmation,
      })

      setMessage(res.data.message || "Password has been reset successfully. Redirecting to login...")
      setTimeout(() => navigate("/login"), 3000) // 3 সেকেন্ড পর লগইন পেজে রিডাইরেক্ট
    } catch (err) {
      if (err.response && err.response.data && err.response.data.errors) {
        // Laravel validation errors (status 422)
        const errors = err.response.data.errors
        let errorMessage = ""
        for (const key in errors) {
          errorMessage += `${errors[key].join(" ")}\n`
        }
        setError(errorMessage.trim())
      } else if (err.response && err.response.data && err.response.data.message) {
        // Other API errors with a message
        setError(err.response.data.message)
      } else {
        // Generic error for network issues or unexpected responses
        setError("Failed to reset password. Please check your network and try again.")
      }
      console.error("Reset password error:", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: "100%", borderRadius: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3, textAlign: "center", fontWeight: "bold" }}>
          Reset Password
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="New Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            required
            error={!!(error && error.includes("password"))} // পাসওয়ার্ড এরর থাকলে দেখাবে
            helperText={error.includes("password") ? error : ""}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            required
            error={!!(error && error.includes("password confirmation"))} // নিশ্চিত পাসওয়ার্ড এরর থাকলে দেখাবে
            helperText={error.includes("password confirmation") ? error : ""}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mb: 2, py: 1.5 }}
            disabled={loading || !isValidLink} // লোডিং বা লিংক অবৈধ হলে বাটন ডিসেবল
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
          <Box sx={{ textAlign: "center" }}>
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Button variant="text" sx={{ textTransform: "none" }}>
                Back to Login
              </Button>
            </Link>
          </Box>
          {message && (
            <Alert severity="success" sx={{ mt: 3 }}>
              {message}
            </Alert>
          )}
          {error && !message && (
            <Alert severity="error" sx={{ mt: 3, whiteSpace: "pre-wrap" }}>
              {error}
            </Alert>
          )}
        </form>
      </Paper>
    </Box>
  )
}

export default ResetPassword
